import { useState, useEffect, useCallback } from 'react';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Users,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ChevronRight,
  X,
  Shield,
} from 'lucide-react';
import { toast } from '@theonefamily/ui';
import { get, post } from '../lib/api.js';

const API_BASE = '/api';

// ─── Local Types ──────────────────────────────────────────────

interface UserSecurityProfile {
  id: string;
  user_id: string;
  user_email: string;
  department?: string;
  role?: string;
  total_phishing_campaigns: number;
  phishing_clicked_count: number;
  phishing_reported_count: number;
  phishing_click_rate: number;
  phishing_report_rate: number;
  recent_click_types: string[];
  total_training_assigned: number;
  training_completed_count: number;
  training_overdue_count: number;
  training_completion_rate: number;
  avg_training_score: number;
  security_score: number;
  risk_level: 'critical' | 'high' | 'medium' | 'low';
  next_recommended_training?: string;
  recommendation_reason?: string;
  recommendation_generated_at?: string;
  last_computed_at: string;
}

interface LearningPathStep {
  step_index: number;
  training_title: string;
  training_type: string;
  reason: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'skipped';
  completed_at?: string;
  score?: number;
}

interface LearningPath {
  id: string;
  user_id: string;
  user_email: string;
  name: string;
  description?: string;
  status: 'active' | 'completed' | 'paused';
  steps: LearningPathStep[];
  current_step_index: number;
  completion_rate: number;
  auto_generated: boolean;
  created_at: string;
}

interface SummaryStats {
  total_users: number;
  by_risk_level: { critical: number; high: number; medium: number; low: number };
  avg_security_score: number;
  users_needing_training: number;
  improvement_this_month: number;
}

interface ProfilesResponse {
  data: UserSecurityProfile[];
  hasMore?: boolean;
}

interface UserDetailResponse {
  data: {
    profile: UserSecurityProfile;
    learning_paths: LearningPath[];
  };
}

// ─── Helpers ──────────────────────────────────────────────────

function riskBadgeClasses(level: string): string {
  switch (level) {
    case 'critical': return 'bg-red-100 text-red-700 border border-red-200';
    case 'high': return 'bg-orange-100 text-orange-700 border border-orange-200';
    case 'medium': return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    case 'low': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function stepStatusClasses(status: string): string {
  switch (status) {
    case 'completed': return 'bg-emerald-100 text-emerald-700';
    case 'in_progress': return 'bg-blue-100 text-blue-700';
    case 'assigned': return 'bg-yellow-100 text-yellow-700';
    case 'skipped': return 'bg-gray-100 text-gray-500';
    default: return 'bg-gray-100 text-gray-400';
  }
}

function scoreBarColor(score: number): string {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 50) return 'bg-yellow-500';
  if (score >= 25) return 'bg-orange-500';
  return 'bg-red-500';
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

// ─── Slide-Out Panel ──────────────────────────────────────────

interface UserDetailPanelProps {
  userId: string;
  onClose: () => void;
}

function UserDetailPanel({ userId, onClose }: UserDetailPanelProps) {
  const [data, setData] = useState<{ profile: UserSecurityProfile; learning_paths: LearningPath[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await get<UserDetailResponse>(`${API_BASE}/protect/adaptive-learning/${userId}`);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  async function handleGenerateRecommendation() {
    setGenerating(true);
    try {
      await post(`${API_BASE}/protect/adaptive-learning/${userId}?action=generate_recommendation`);
      await fetchDetail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate recommendation');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="w-[520px] bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">User Security Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading...</div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
          )}

          {data && (
            <>
              {/* Profile Header */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{data.profile.user_email}</p>
                    {data.profile.department && (
                      <p className="text-xs text-gray-500 mt-0.5">{data.profile.department}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${riskBadgeClasses(data.profile.risk_level)}`}>
                    {data.profile.risk_level}
                  </span>
                </div>

                {/* Security score bar */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Security Score</span>
                    <span className="text-sm font-bold text-gray-900">{data.profile.security_score}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${scoreBarColor(data.profile.security_score)}`}
                      style={{ width: `${data.profile.security_score}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Phishing Click Rate</p>
                  <p className={`text-lg font-bold ${data.profile.phishing_click_rate > 0.3 ? 'text-red-600' : data.profile.phishing_click_rate > 0.1 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                    {pct(data.profile.phishing_click_rate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {data.profile.phishing_clicked_count}/{data.profile.total_phishing_campaigns} campaigns
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Training Completion</p>
                  <p className={`text-lg font-bold ${data.profile.training_completion_rate >= 0.7 ? 'text-emerald-600' : data.profile.training_completion_rate >= 0.4 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {pct(data.profile.training_completion_rate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {data.profile.training_completed_count}/{data.profile.total_training_assigned} assigned
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Avg Training Score</p>
                  <p className="text-lg font-bold text-gray-900">{data.profile.avg_training_score || '—'}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Overdue Trainings</p>
                  <p className={`text-lg font-bold ${data.profile.training_overdue_count > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {data.profile.training_overdue_count}
                  </p>
                </div>
              </div>

              {/* Recent Click Types */}
              {data.profile.recent_click_types.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Recent Click Patterns</p>
                  <div className="flex flex-wrap gap-2">
                    {data.profile.recent_click_types.map((t) => (
                      <span key={t} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded-full border border-red-100 capitalize">
                        {t.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Recommendation */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800">AI Recommendation</span>
                  </div>
                  <button
                    onClick={() => void handleGenerateRecommendation()}
                    disabled={generating}
                    className="text-xs text-emerald-700 hover:text-emerald-900 underline disabled:opacity-50"
                  >
                    {generating ? 'Generating...' : 'Refresh'}
                  </button>
                </div>
                {data.profile.next_recommended_training ? (
                  <>
                    <p className="text-sm font-semibold text-emerald-900">{data.profile.next_recommended_training}</p>
                    {data.profile.recommendation_reason && (
                      <p className="text-xs text-emerald-700 mt-1">{data.profile.recommendation_reason}</p>
                    )}
                    {data.profile.recommendation_generated_at && (
                      <p className="text-xs text-emerald-500 mt-1">
                        Generated {new Date(data.profile.recommendation_generated_at).toLocaleDateString()}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-emerald-700">No recommendation yet. Click Refresh to generate.</p>
                )}
              </div>

              {/* Learning Paths */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Learning Paths</p>
                {data.learning_paths.length === 0 ? (
                  <p className="text-sm text-gray-500">No learning paths assigned yet.</p>
                ) : (
                  <div className="space-y-3">
                    {data.learning_paths.map((path) => (
                      <div key={path.id} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{path.name}</p>
                            {path.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{path.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{Math.round(path.completion_rate * 100)}%</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                              path.status === 'active' ? 'bg-blue-100 text-blue-700' :
                              path.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {path.status}
                            </span>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {path.steps.map((step) => (
                            <div key={step.step_index} className="flex items-center gap-3 px-3 py-2">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                {step.step_index + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">{step.training_title}</p>
                                <p className="text-xs text-gray-400 capitalize">{step.training_type}</p>
                              </div>
                              <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${stepStatusClasses(step.status)}`}>
                                {step.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400">
                Profile computed: {data.profile.last_computed_at ? new Date(data.profile.last_computed_at).toLocaleString() : '—'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

type FilterTab = 'all' | 'critical' | 'high' | 'at_risk';

export function AdaptiveLearningPage() {
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [profiles, setProfiles] = useState<UserSecurityProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, profilesRes] = await Promise.all([
        get<{ data: SummaryStats }>(`${API_BASE}/protect/adaptive-learning?action=summary`),
        get<ProfilesResponse>(`${API_BASE}/protect/adaptive-learning`),
      ]);
      setSummary(summaryRes.data);
      setProfiles(profilesRes.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load adaptive learning data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function handleRefreshAll() {
    setRefreshing(true);
    try {
      await post(`${API_BASE}/protect/adaptive-learning?action=refresh_profile`, {});
      await fetchData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to refresh profiles');
    } finally {
      setRefreshing(false);
    }
  }

  // Filter profiles based on active tab
  const filteredProfiles = profiles.filter((p) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'critical') return p.risk_level === 'critical';
    if (activeTab === 'high') return p.risk_level === 'high';
    if (activeTab === 'at_risk') return p.phishing_click_rate > 0.2;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-sm">Loading adaptive learning data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 text-sm">{error}</p>
        <button onClick={() => void fetchData()} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Adaptive Learning Engine</h2>
          <p className="text-gray-600 mt-1 text-sm">
            AI-powered personalized security training based on each user's phishing history and training behavior.
          </p>
        </div>
        <button
          onClick={() => void handleRefreshAll()}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh All Profiles'}
        </button>
      </div>

      {/* Summary Stats Bar */}
      {summary && (
        <div className="grid grid-cols-5 gap-3 mt-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">Total Users</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{summary.total_users}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-2">By Risk Level</p>
            <div className="flex flex-wrap gap-1">
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                {summary.by_risk_level.critical} critical
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                {summary.by_risk_level.high} high
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                {summary.by_risk_level.medium} medium
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                {summary.by_risk_level.low} low
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">Avg Security Score</span>
            </div>
            <div className="flex items-center gap-2">
              <p className={`text-2xl font-bold ${summary.avg_security_score >= 70 ? 'text-emerald-600' : summary.avg_security_score >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                {summary.avg_security_score}
              </p>
              {summary.improvement_this_month >= 50
                ? <TrendingUp className="w-4 h-4 text-emerald-500" />
                : <TrendingDown className="w-4 h-4 text-red-400" />
              }
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-gray-500">Need Training</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{summary.users_needing_training}</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-gray-500">Improved This Month</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{summary.improvement_this_month}%</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {(['all', 'critical', 'high', 'at_risk'] as const).map((tab) => {
          const labels: Record<FilterTab, string> = {
            all: 'All',
            critical: 'Critical',
            high: 'High',
            at_risk: 'At Risk (>20% click)',
          };
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* Profiles Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Department</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Risk Level</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Security Score</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Click Rate</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Training</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">AI Recommendation</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProfiles.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  {profiles.length === 0
                    ? 'No user profiles yet. Profiles are generated automatically from phishing and training data.'
                    : 'No users match the selected filter.'}
                </td>
              </tr>
            ) : (
              filteredProfiles.map((profile) => (
                <tr
                  key={profile.user_id}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedUserId(profile.user_id)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 truncate max-w-[180px]">{profile.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{profile.department ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${riskBadgeClasses(profile.risk_level)}`}>
                      {profile.risk_level}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${scoreBarColor(profile.security_score)}`}
                          style={{ width: `${profile.security_score}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 tabular-nums">{profile.security_score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${profile.phishing_click_rate > 0.3 ? 'text-red-600' : profile.phishing_click_rate > 0.1 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                      {pct(profile.phishing_click_rate)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${profile.training_completion_rate >= 0.7 ? 'text-emerald-600' : profile.training_completion_rate >= 0.4 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {pct(profile.training_completion_rate)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {profile.next_recommended_training ? (
                      <div className="flex items-center gap-1.5">
                        <Brain className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate max-w-[160px]">
                          {profile.next_recommended_training}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedUserId(profile.user_id); }}
                      className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-0.5 ml-auto"
                    >
                      View <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Slide-out Panel */}
      {selectedUserId && (
        <UserDetailPanel
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}
